<template>
  <v-card>
    <v-card-title>
      <v-row class="py-4 px-4">
        <v-col>
          <span v-if="dialog.config.hasTitle">VERIFICAR E-MAIL</span>
        </v-col>
        <v-spacer />
        <v-btn v-if="dialog.config.hasCloseButton" icon elevation="0">
          <v-icon size="small" @click="close">
            mdi-close
          </v-icon>
        </v-btn>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-row justify="center" class="my-4">
        <v-col cols="6">
          <t-logo />
        </v-col>
      </v-row>
      <v-form
        ref="form"
        v-model="valid"
        class="py-4"
        @submit.prevent="submit"
        @keypress.enter="submit"
      >
        <v-row justify="center">
          <v-col cols="12" class="text-subtitle-1">
            Para garantir um processo de compra mais seguro precisamos verificar o seu e-mail.
            <br>
            <br>
            Enviamos um código de verificação para o endereço <strong>{{ anonymizeEmail(user.email) }}</strong>. Verifique sua caixa de entrada e insira o código para ativar sua conta.
          </v-col>
          <v-col v-if="!loading" cols="12" md="6">
            <v-otp-input
              v-model="verificationCode"
              :disabled="loading"
              :length="6"
              class="mt-4"
              hide-details
              required
            />
          </v-col>
          <v-col cols="12">
            <v-row>
              <v-col cols="12" md="6" class="px-2">
                <t-btn
                  color="primary"
                  block
                  class="heading--text"
                  large
                  :loading="loading"
                  :disabled="!valid"
                  @click="send"
                >
                  Reenviar Código
                </t-btn>
              </v-col>
              <v-col cols="12" md="6" class="px-2">
                <t-btn
                  color="success"
                  block
                  type="submit"
                  class="heading--text"
                  large
                  :loading="loading"
                  :disabled="!valid"
                >
                  Verificar E-mail
                </t-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, type Ref } from "vue";
import type User from "@/types/User";
import TBtn from "@/components/core/TBtn.vue";
import TLogo from "@/components/core/TLogo.vue";
import EmailService from "@/services/EmailService";
import getUserData from "@/api/requests/getUserData";
import { anonymizeEmail } from "@/filters/anonymize-email";
import { useDialog } from "@/stores/dialog";
import { useLoader } from "@/stores/loader";
import { useSnackbar } from "@/stores/snackbar";
import { useUserStore } from "@/stores/user";

defineOptions({
  name: "FormEmailVerification",
});

const userStore = useUserStore();
const dialog = useDialog();
const loader = useLoader();
const snackbar = useSnackbar();

const valid: Ref<boolean> = ref(true);
const verificationCode: Ref<string> = ref("");

const user = computed<User>(() => userStore.data);

const loading = computed<boolean>(() => loader.loading());

async function send(): Promise<void> {
  loader.increment();
  try {
    await new EmailService().send(user.value.document);
  } catch (e) {
    console.log(e);
  } finally {
    loader.decrement();
  }
}

async function submit(): Promise<void> {
  loader.increment();
  try {
    const code = verificationCode.value.trim();

    if (!code) {
      snackbar.show("Por favor, insira o código de verificação!", "error");
      return;
    }

    await new EmailService().verify(user.value.document, code);
    snackbar.show("E-mail verificado com sucesso!", "success");
    const userData = (await getUserData()).data;
    userStore.setUser(userData);
    close();
  } catch {
    snackbar.show("Ocorreu um erro ao verificar o código. Tente novamente mais tarde.", "error");
  } finally {
    loader.decrement();
  }
}

function close(): void {
  dialog.hide();
}

onMounted(() => {
  send();
});
</script>
